// Data source selector API models and functions
import { authenticatedFetch } from '../utils/authApi';
import { cachedApiService } from '../services/CachedApiService';

export interface DataSourceCategory {
  name: string;
  sources: DataSource[];
}

export interface DataSource {
  id: string;
  name: string;
  type: string;
  description?: string;
}

export interface AvailableDataSources {
  categories: DataSourceCategory[];
}

export interface UserPreferences {
  use_dynamic_system_message: boolean;
  selected_data_source?: string | null;
  selected_model?: string | null;
}

export interface DataSourceRequest {
  selected_data_source?: string | null;
  use_dynamic_system_message?: boolean;
  selected_model?: string | null;
}

// API functions - using cached API service to reduce duplicate calls
export async function getAvailableDataSources(forceRefresh: boolean = false): Promise<AvailableDataSources> {
  // Invalidate cache when force refresh is requested (e.g., after creating a Research Scope)
  if (forceRefresh) {
    cachedApiService.invalidate('/api/data-sources/available');
  }
  
  const response = await cachedApiService.cachedFetch('/api/data-sources/available');
  if (!response.ok) {
    throw new Error(`Failed to fetch available data sources: ${response.statusText}`);
  }
  const data = await response.json();
  
  // Transform backend response to match frontend interface
  return {
    categories: data.data_sources || []
  };
}

export async function getUserPreferences(): Promise<UserPreferences> {
  const response = await cachedApiService.cachedFetch('/api/data-sources/user-preferences');
  if (!response.ok) {
    throw new Error(`Failed to fetch user preferences: ${response.statusText}`);
  }
  const data = await response.json();
  
  // API now returns preferences directly (not nested in "preferences" object)
  // Default to true to match backend default (dynamic_system_message_enabled defaults to True)
  const result = {
    use_dynamic_system_message: data.use_dynamic_system_message ?? true,
    // Normalize deprecated internal_knowledge to general_knowledge
    selected_data_source: (data.selected_data_source === "internal_knowledge" || data.selected_data_source === "general_knowledge") ? undefined : data.selected_data_source,
    selected_model: data.selected_model
  };
  
  return result;
}

export async function updateUserPreferences(preferences: Partial<UserPreferences>): Promise<UserPreferences> {
  const requestBody = {
    ...preferences
  };

  // POST requests bypass cache but still deduplicate
  const response = await authenticatedFetch('/api/data-sources/user-preferences', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestBody)
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error('updateUserPreferences failed:', response.status, errorText);
    throw new Error(`Failed to update user preferences: ${response.statusText}`);
  }
  
  // Invalidate the cache after successful update
  cachedApiService.invalidate('/api/data-sources/user-preferences');
  
  const result = await response.json();
  return result;
}